import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  useTheme,
  Grid,
  Paper,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Person as PersonIcon,
  PersonAdd as GuestIcon,
  AdminPanelSettings as AdminIcon,
  Close as CloseIcon,
  Login as LoginIcon,
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Phone as PhoneIcon,
  LockReset as LockResetIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import authService from '../services/authService';

const LoginPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { login, gustLogin, gustRegister, isLoggingIn, isGustLoggingIn, isGustRegistering } = useAuth();
  
  // State for dialogs
  const [userLoginOpen, setUserLoginOpen] = useState(false);
  const [guestLoginOpen, setGuestLoginOpen] = useState(false);
  
  // State for guest tabs
  const [guestActiveTab, setGuestActiveTab] = useState(0);
  
  // Error states
  const [userLoginError, setUserLoginError] = useState('');
  const [guestLoginError, setGuestLoginError] = useState('');
  
  // Password visibility
  const [showPassword, setShowPassword] = useState(false);
  
  // Forgot password states
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState('email'); // 'email', 'otp', 'reset'
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState('');
  const [isForgotPasswordLoading, setIsForgotPasswordLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [forgotPasswordMethod, setForgotPasswordMethod] = useState('otp'); // 'otp' or 'link'
  
  // Form hooks
  const userForm = useForm();
  const guestForm = useForm();
  const forgotPasswordForm = useForm();

  // Handle user login
  const handleUserLogin = async (data) => {
    try {
      setUserLoginError('');
      await login(data);
      setUserLoginOpen(false);
    } catch (err) {
      setUserLoginError(err.response?.data?.message || 'Login failed');
    }
  };

  // Handle guest login
  const handleGuestLogin = async (data) => {
    try {
      setGuestLoginError('');
      await gustLogin(data);
      setGuestLoginOpen(false);
    } catch (err) {
      setGuestLoginError(err.response?.data?.message || 'Login failed');
    }
  };

  // Handle guest register
  const handleGuestRegister = async (data) => {
    try {
      setGuestLoginError('');
      await gustRegister(data);
      setGuestLoginOpen(false);
    } catch (err) {
      setGuestLoginError(err.response?.data?.message || 'Registration failed');
    }
  };

  // Handle admin login redirect
  const handleAdminLogin = () => {
    window.open('https://seethakalyanavaibhogame.com/skvadmin/', '_blank');
  };

  // Handle guest tab change
  const handleGuestTabChange = (event, newValue) => {
    setGuestActiveTab(newValue);
    setGuestLoginError('');
    guestForm.reset();
  };

  // Forgot password handlers
  const handleForgotPasswordEmail = async (data) => {
    try {
      setIsForgotPasswordLoading(true);
      setForgotPasswordError('');
      setForgotPasswordSuccess('');
      
      const response = await authService.sendPasswordOtp(data.email, 'user', forgotPasswordMethod);
      
      if (response.status === 1) {
        setForgotPasswordEmail(data.email);
        if (forgotPasswordMethod === 'otp') {
          setForgotPasswordStep('otp');
          setForgotPasswordSuccess(response.message);
        } else {
          setForgotPasswordSuccess('Password reset link has been sent to your email. Please check your inbox and click the link to reset your password.');
          setTimeout(() => {
            handleCloseForgotPassword();
          }, 3000);
        }
      } else {
        setForgotPasswordError(response.message);
      }
    } catch (err) {
      setForgotPasswordError(err.response?.data?.message || `Failed to send ${forgotPasswordMethod === 'otp' ? 'OTP' : 'reset link'}`);
    } finally {
      setIsForgotPasswordLoading(false);
    }
  };

  const handleForgotPasswordOtp = async (data) => {
    try {
      setIsForgotPasswordLoading(true);
      setForgotPasswordError('');
      setForgotPasswordSuccess('');
      
      const response = await authService.validatePasswordOtp(forgotPasswordEmail, data.otp, 'user');
      
      if (response.status === 1) {
        setForgotPasswordStep('reset');
        setForgotPasswordSuccess(response.message);
      } else {
        setForgotPasswordError(response.message);
      }
    } catch (err) {
      setForgotPasswordError(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setIsForgotPasswordLoading(false);
    }
  };

  const handleResetPassword = async (data) => {
    try {
      setIsForgotPasswordLoading(true);
      setForgotPasswordError('');
      setForgotPasswordSuccess('');
      
      if (data.password !== data.confirmPassword) {
        setForgotPasswordError('Passwords do not match');
        return;
      }
      
      const response = await authService.resetPassword(forgotPasswordEmail, data.password, data.confirmPassword, 'user');
      
      if (response.status === 1) {
        setForgotPasswordSuccess('Password reset successfully! You can now login with your new password.');
        setTimeout(() => {
          handleCloseForgotPassword();
          setUserLoginOpen(true);
        }, 2000);
      } else {
        setForgotPasswordError(response.message);
      }
    } catch (err) {
      setForgotPasswordError(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setIsForgotPasswordLoading(false);
    }
  };

  const handleCloseForgotPassword = () => {
    setForgotPasswordOpen(false);
    setForgotPasswordStep('email');
    setForgotPasswordEmail('');
    setForgotPasswordError('');
    setForgotPasswordSuccess('');
    setIsForgotPasswordLoading(false);
    setForgotPasswordMethod('otp');
    forgotPasswordForm.reset();
  };

  const handleBackToEmail = () => {
    setForgotPasswordStep('email');
    setForgotPasswordError('');
    setForgotPasswordSuccess('');
    forgotPasswordForm.reset();
  };

  const handleBackToOtp = () => {
    setForgotPasswordStep('otp');
    setForgotPasswordError('');
    setForgotPasswordSuccess('');
    forgotPasswordForm.reset();
  };

  // Reset forms when dialogs close
  const handleCloseUserLogin = () => {
    setUserLoginOpen(false);
    setUserLoginError('');
    userForm.reset();
  };

  const handleCloseGuestLogin = () => {
    setGuestLoginOpen(false);
    setGuestLoginError('');
    setGuestActiveTab(0);
    guestForm.reset();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
        py: { xs: 4, md: 8 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 0% 0%, rgba(92,15,47,0.05) 0%, transparent 50%)',
          zIndex: 0
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '50%',
          right: '-10%',
          width: '40%',
          height: '40%',
          background: 'radial-gradient(circle, rgba(92,15,47,0.1) 0%, transparent 70%)',
          transform: 'translateY(-50%)',
          zIndex: 0
        }
      }}
    >
      {/* Navigation Buttons */}
      <Box
        sx={{
          position: 'absolute',
          top: 20,
          left: 20,
          zIndex: 10,
          display: 'flex',
          gap: 2
        }}
      >
        <IconButton
          onClick={() => navigate('/')}
          sx={{
            bgcolor: 'rgba(255,255,255,0.9)',
            '&:hover': {
              bgcolor: 'white',
              transform: 'scale(1.1)'
            },
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            transition: 'all 0.3s'
          }}
        >
          <HomeIcon />
        </IconButton>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            bgcolor: 'rgba(255,255,255,0.9)',
            '&:hover': {
              bgcolor: 'white',
              transform: 'scale(1.1)'
            },
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            transition: 'all 0.3s'
          }}
        >
          <ArrowBackIcon />
        </IconButton>
      </Box>
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4} alignItems="center" justifyContent="center">
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                textAlign: 'center',
                mb: { xs: 4, md: 0 }
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  color: '#333',
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                  background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 2px 10px rgba(92,15,47,0.2)'
                }}
              >
                Welcome to Seetha Kalyana Vaibhogame
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: '#666',
                  mb: 4,
                  fontSize: { xs: '1rem', md: '1.25rem' }
                }}
              >
                Choose your login option to continue your journey to find your perfect match
              </Typography>
              <Box sx={{ 
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 3,
                mb: 4
              }}>
                {/* <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#5c0f2f',
                  p: 2,
                  borderRadius: '12px',
                  bgcolor: 'rgba(255,255,255,0.9)',
                  boxShadow: '0 4px 15px rgba(92,15,47,0.1)',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 25px rgba(92,15,47,0.2)'
                  }
                }}>
                  <PersonIcon />
                  <Typography>User Access</Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#5c0f2f',
                  p: 2,
                  borderRadius: '12px',
                  bgcolor: 'rgba(255,255,255,0.9)',
                  boxShadow: '0 4px 15px rgba(92,15,47,0.1)',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 25px rgba(92,15,47,0.2)'
                  }
                }}>
                  <GuestIcon />
                  <Typography>Guest Access</Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#5c0f2f',
                  p: 2,
                  borderRadius: '12px',
                  bgcolor: 'rgba(255,255,255,0.9)',
                  boxShadow: '0 4px 15px rgba(92,15,47,0.1)',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 25px rgba(92,15,47,0.2)'
                  }
                }}>
                  <AdminIcon />
                  <Typography>Admin Access</Typography>
                </Box> */}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: '16px',
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 8px 25px rgba(92,15,47,0.2)'
                }
              }}
            >

              <Typography
                component="h1"
                variant="h4"
                sx={{
                  textAlign: 'center',
                  mb: 3,
                  fontWeight: 700,
                  color: '#333',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '50px',
                    height: '3px',
                    background: 'linear-gradient(90deg, #5c0f2f, #7d1f4f)',
                    borderRadius: '2px'
                  }
                }}
              >
                Sign In
              </Typography>

              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3,
                justifyContent: 'center',
                alignItems: 'stretch',
                flexWrap: 'wrap'
              }}>
                {/* User Login Button */}
                <Card
                  elevation={4}
                  sx={{
                    flex: { xs: '1 1 100%', md: '1 1 30%' },
                    maxWidth: { xs: '100%', md: '350px' },
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, rgba(92,15,47,0.05) 0%, rgba(125,31,79,0.05) 100%)',
                    border: '2px solid transparent',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 40px rgba(92,15,47,0.2)',
                      border: '2px solid rgba(92,15,47,0.3)',
                      background: 'linear-gradient(135deg, rgba(92,15,47,0.1) 0%, rgba(125,31,79,0.1) 100%)',
                    },
                  }}
                  onClick={() => setUserLoginOpen(true)}
                >
                  <CardContent sx={{ textAlign: 'center', p: 4 }}>
                    <Box sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      boxShadow: '0 4px 15px rgba(92,15,47,0.3)'
                    }}>
                      <PersonIcon
                        sx={{
                          fontSize: 40,
                          color: 'white',
                        }}
                      />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#5c0f2f' }}>
                      User Login
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Login with your registered account to access all features
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{
                        background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #4a0d26, #5c0f2f)',
                        },
                        textTransform: 'none',
                        borderRadius: '8px',
                        px: 3
                      }}
                    >
                      Sign In
                    </Button>
                  </CardContent>
                </Card>

                {/* Guest User Login Button */}
                <Card
                  elevation={4}
                  sx={{
                    flex: { xs: '1 1 100%', md: '1 1 30%' },
                    maxWidth: { xs: '100%', md: '350px' },
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, rgba(92,15,47,0.05) 0%, rgba(125,31,79,0.05) 100%)',
                    border: '2px solid transparent',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 40px rgba(92,15,47,0.2)',
                      border: '2px solid rgba(92,15,47,0.3)',
                      background: 'linear-gradient(135deg, rgba(92,15,47,0.1) 0%, rgba(125,31,79,0.1) 100%)',
                    },
                  }}
                  onClick={() => setGuestLoginOpen(true)}
                >
                  <CardContent sx={{ textAlign: 'center', p: 4 }}>
                    <Box sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      boxShadow: '0 4px 15px rgba(92,15,47,0.3)'
                    }}>
                      <GuestIcon
                        sx={{
                          fontSize: 40,
                          color: 'white',
                        }}
                      />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#5c0f2f' }}>
                      Guest Login
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Browse profiles as a guest with limited access
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{
                        background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #4a0d26, #5c0f2f)',
                        },
                        textTransform: 'none',
                        borderRadius: '8px',
                        px: 3
                      }}
                    >
                      Guest Access
                    </Button>
                  </CardContent>
                </Card>

                {/* Admin Login Button */}
                <Card
                  elevation={4}
                  sx={{
                    flex: { xs: '1 1 100%', md: '1 1 30%' },
                    maxWidth: { xs: '100%', md: '350px' },
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, rgba(92,15,47,0.05) 0%, rgba(125,31,79,0.05) 100%)',
                    border: '2px solid transparent',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 40px rgba(92,15,47,0.2)',
                      border: '2px solid rgba(92,15,47,0.3)',
                      background: 'linear-gradient(135deg, rgba(92,15,47,0.1) 0%, rgba(125,31,79,0.1) 100%)',
                    },
                  }}
                  onClick={handleAdminLogin}
                >
                  <CardContent sx={{ textAlign: 'center', p: 4 }}>
                    <Box sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      boxShadow: '0 4px 15px rgba(92,15,47,0.3)'
                    }}>
                      <AdminIcon
                        sx={{
                          fontSize: 40,
                          color: 'white',
                        }}
                      />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#5c0f2f' }}>
                       Partner Login
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Access admin &  Partner panel for system management
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{
                        background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #4a0d26, #5c0f2f)',
                        },
                        textTransform: 'none',
                        borderRadius: '8px',
                        px: 3
                      }}
                    >
                       Partner Login
                    </Button>
                  </CardContent>
                </Card>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* User Login Dialog */}
      <Dialog
        open={userLoginOpen}
        onClose={handleCloseUserLogin}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: '16px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: 'linear-gradient(135deg, #5c0f2f 0%, #7d1f4f 100%)',
          color: 'white',
          borderRadius: '16px 16px 0 0',
          p: 3
        }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Sign In
          </Typography>
          <IconButton onClick={handleCloseUserLogin} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 4, mt:4 }}>
          {userLoginError && (
            <Typography
              color="error"
              sx={{
                textAlign: 'center',
                mb: 3,
                p: 2,
                mt:5,
                bgcolor: 'rgba(255, 0, 0, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(255, 0, 0, 0.2)'
              }}
            >
              {userLoginError}
            </Typography>
          )}
          <Box
            component="form"
            onSubmit={userForm.handleSubmit(handleUserLogin)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              mt: 6.25 
            }}
          >
            <TextField
              fullWidth
              label="Email Address"
              autoComplete="email"
              autoFocus
              {...userForm.register('email', { required: 'Email is required' })}
              error={!!userForm.formState.errors.email}
              helperText={userForm.formState.errors.email?.message}
              InputProps={{
                startAdornment: <EmailIcon sx={{ color: '#5c0f2f', mr: 1 ,mt: 0.5 }} />
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '&:hover fieldset': {
                    borderColor: '#5c0f2f',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#5c0f2f',
                    borderWidth: 2
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#5c0f2f'
                }
              }}
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              {...userForm.register('password', { required: 'Password is required' })}
              error={!!userForm.formState.errors.password}
              helperText={userForm.formState.errors.password?.message}
              InputProps={{
                startAdornment: <LockIcon sx={{ color: '#5c0f2f', mr: 1 }} />,
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: '#5c0f2f' }}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '&:hover fieldset': {
                    borderColor: '#5c0f2f',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#5c0f2f',
                    borderWidth: 2
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#5c0f2f'
                }
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoggingIn}
              sx={{
                mt: 2,
                py: 2,
                background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #4a0d26, #5c0f2f)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(92,15,47,0.3)'
                },
                '&:disabled': {
                  background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                  opacity: 0.7
                },
                fontSize: '1.1rem',
                textTransform: 'none',
                borderRadius: '12px',
                transition: 'all 0.3s',
                fontWeight: 600
              }}
            >
              {isLoggingIn ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                  Signing In...
                </Box>
              ) : (
                'Sign In'
              )}
            </Button>
            
            <Box sx={{ 
              textAlign: 'center',
              mt: 2,
              p: 2,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, rgba(92,15,47,0.08) 0%, rgba(125,31,79,0.08) 100%)',
              border: '1px solid rgba(92,15,47,0.2)',
            }}>
              <Button 
                variant="text" 
                sx={{ 
                  color: '#5c0f2f', 
                  textTransform: 'none',
                  fontWeight: 600,
                  mb: 1,
                  '&:hover': {
                    background: 'rgba(92,15,47,0.1)'
                  }
                }}
                onClick={() => {
                  handleCloseUserLogin();
                  setForgotPasswordOpen(true);
                }}
                startIcon={<LockResetIcon />}
              >
                Forgot Password?
              </Button>
            </Box>
            
            <Box sx={{ 
              textAlign: 'center',
              mt: 1,
              p: 2,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, rgba(92,15,47,0.08) 0%, rgba(125,31,79,0.08) 100%)',
              border: '1px solid rgba(92,15,47,0.2)',
            }}>
              <Typography variant="body2" sx={{ color: '#5c0f2f', fontWeight: 500 }}>
                Don't have an account? 
                <Button 
                  variant="text" 
                  sx={{ 
                    color: '#5c0f2f', 
                    textTransform: 'none',
                    fontWeight: 600,
                    ml: 1,
                    '&:hover': {
                      background: 'rgba(92,15,47,0.1)'
                    }
                  }}
                  onClick={() => {
                    handleCloseUserLogin();
                    navigate('/register');
                  }}
                >
                  Sign Up
                </Button>
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Guest Login Dialog */}
      <Dialog
        open={guestLoginOpen}
        onClose={handleCloseGuestLogin}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: '16px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: 'linear-gradient(135deg, #5c0f2f 0%, #7d1f4f 100%)',
          color: 'white',
          borderRadius: '16px 16px 0 0',
          p: 3
        }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Guest Access
          </Typography>
          <IconButton onClick={handleCloseGuestLogin} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
          
          <DialogContent sx={{ p: 4 }}>
            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs 
                value={guestActiveTab} 
                onChange={handleGuestTabChange}
                sx={{
                  '& .MuiTab-root': {
                    color: '#666',
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1rem',
                  },
                  '& .Mui-selected': {
                    color: '#5c0f2f',
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#5c0f2f',
                  },
                }}
              >
                <Tab label="Login as Guest" />
                <Tab label="Register as Guest" />
              </Tabs>
            </Box>

            {guestLoginError && (
              <Typography
                color="error"
                sx={{
                  textAlign: 'center',
                  mb: 2,
                  p: 1,
                  bgcolor: 'rgba(255, 0, 0, 0.1)',
                  borderRadius: '4px'
                }}
              >
                {guestLoginError}
              </Typography>
            )}

            {/* Login Tab */}
            {guestActiveTab === 0 && (
              <Box
                component="form"
                onSubmit={guestForm.handleSubmit(handleGuestLogin)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2
                }}
              >
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  {...guestForm.register('email', { required: 'Email is required' })}
                  error={!!guestForm.formState.errors.email}
                  helperText={guestForm.formState.errors.email?.message}
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ color: '#5c0f2f', mr: 1 }} />
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#5c0f2f',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#5c0f2f',
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#5c0f2f'
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="Mobile Number"
                  {...guestForm.register('mobile', { required: 'Mobile number is required' })}
                  error={!!guestForm.formState.errors.mobile}
                  helperText={guestForm.formState.errors.mobile?.message}
                  InputProps={{
                    startAdornment: <PhoneIcon sx={{ color: '#5c0f2f', mr: 1 }} />
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#5c0f2f',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#5c0f2f',
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#5c0f2f'
                    }
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isGustLoggingIn}
                  sx={{
                    mt: 2,
                    py: 1.5,
                    background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #4d0d28, #601233)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 15px rgba(92,15,47,0.3)'
                    },
                    '&:disabled': {
                      background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                      opacity: 0.7
                    },
                    fontSize: '1.1rem',
                    textTransform: 'none',
                    borderRadius: '8px',
                    transition: 'all 0.3s'
                  }}
                >
                  {isGustLoggingIn ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} sx={{ color: 'white' }} />
                      Logging in...
                    </Box>
                  ) : (
                    'Login as Guest'
                  )}
                </Button>
              </Box>
            )}

            {/* Register Tab */}
            {guestActiveTab === 1 && (
              <Box
                component="form"
                onSubmit={guestForm.handleSubmit(handleGuestRegister)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2
                }}
              >
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  {...guestForm.register('email', { required: 'Email is required' })}
                  error={!!guestForm.formState.errors.email}
                  helperText={guestForm.formState.errors.email?.message}
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ color: '#5c0f2f', mr: 1 }} />
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#5c0f2f',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#5c0f2f',
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#5c0f2f'
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="Mobile Number"
                  {...guestForm.register('mobile', { required: 'Mobile number is required' })}
                  error={!!guestForm.formState.errors.mobile}
                  helperText={guestForm.formState.errors.mobile?.message}
                  InputProps={{
                    startAdornment: <PhoneIcon sx={{ color: '#5c0f2f', mr: 1 }} />
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#5c0f2f',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#5c0f2f',
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#5c0f2f'
                    }
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isGustRegistering}
                  sx={{
                    mt: 2,
                    py: 1.5,
                    background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #4d0d28, #601233)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 15px rgba(92,15,47,0.3)'
                    },
                    '&:disabled': {
                      background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                      opacity: 0.7
                    },
                    fontSize: '1.1rem',
                    textTransform: 'none',
                    borderRadius: '8px',
                    transition: 'all 0.3s'
                  }}
                >
                  {isGustRegistering ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} sx={{ color: 'white' }} />
                      Submitting...
                    </Box>
                  ) : (
                    'Register as Guest'
                  )}
                </Button>
              </Box>
            )}
          </DialogContent>
        </Dialog>

      {/* Forgot Password Dialog */}
      <Dialog
        open={forgotPasswordOpen}
        onClose={handleCloseForgotPassword}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: '16px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: 'linear-gradient(135deg, #5c0f2f 0%, #7d1f4f 100%)',
          color: 'white',
          borderRadius: '16px 16px 0 0',
          p: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {forgotPasswordStep !== 'email' && (
              <IconButton 
                onClick={forgotPasswordStep === 'otp' ? handleBackToEmail : handleBackToOtp}
                sx={{ color: 'white', mr: 1 }}
              >
                <BackIcon />
              </IconButton>
            )}
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {forgotPasswordStep === 'email' && 'Reset Password'}
              {forgotPasswordStep === 'otp' && 'Verify OTP'}
              {forgotPasswordStep === 'reset' && 'Set New Password'}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseForgotPassword} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 4, mt: 4 }}>
          {forgotPasswordError && (
            <Typography
              color="error"
              sx={{
                textAlign: 'center',
                mb: 3,
                p: 2,
                bgcolor: 'rgba(255, 0, 0, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(255, 0, 0, 0.2)'
              }}
            >
              {forgotPasswordError}
            </Typography>
          )}
          
          {forgotPasswordSuccess && (
            <Typography
              sx={{
                textAlign: 'center',
                mb: 3,
                p: 2,
                color: 'green',
                bgcolor: 'rgba(0, 255, 0, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(0, 255, 0, 0.2)'
              }}
            >
              {forgotPasswordSuccess}
            </Typography>
          )}

          {/* Email Step */}
          {forgotPasswordStep === 'email' && (
            <Box
              component="form"
              onSubmit={forgotPasswordForm.handleSubmit(handleForgotPasswordEmail)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                mt: 2
              }}
            >
              <Typography variant="body1" sx={{ textAlign: 'center', mb: 2, color: '#666' }}>
                {forgotPasswordMethod === 'otp' 
                  ? "Enter your email address and we'll send you an OTP to reset your password."
                  : "Enter your email address and we'll send you a password reset link."
                }
              </Typography>
              
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                autoFocus
                {...forgotPasswordForm.register('email', { 
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
                error={!!forgotPasswordForm.formState.errors.email}
                helperText={forgotPasswordForm.formState.errors.email?.message}
                InputProps={{
                  startAdornment: <EmailIcon sx={{ color: '#5c0f2f', mr: 1 }} />
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&:hover fieldset': {
                      borderColor: '#5c0f2f',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#5c0f2f',
                      borderWidth: 2
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#5c0f2f'
                  }
                }}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isForgotPasswordLoading}
                sx={{
                  mt: 2,
                  py: 2,
                  background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #4a0d26, #5c0f2f)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(92,15,47,0.3)'
                  },
                  '&:disabled': {
                    background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                    opacity: 0.7
                  },
                  fontSize: '1.1rem',
                  textTransform: 'none',
                  borderRadius: '12px',
                  transition: 'all 0.3s',
                  fontWeight: 600
                }}
              >
                {isForgotPasswordLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                    {forgotPasswordMethod === 'otp' ? 'Sending OTP...' : 'Sending Link...'}
                  </Box>
                ) : (
                  forgotPasswordMethod === 'otp' ? 'Send OTP' : 'Send Reset Link'
                )}
              </Button>
              
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  variant="text"
                  onClick={() => {
                    setForgotPasswordMethod(forgotPasswordMethod === 'otp' ? 'link' : 'otp');
                    setForgotPasswordError('');
                    setForgotPasswordSuccess('');
                  }}
                  sx={{
                    color: '#5c0f2f',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'rgba(92,15,47,0.1)'
                    }
                  }}
                >
                  {forgotPasswordMethod === 'otp' 
                    ? 'Try Another Way - Send Reset Link' 
                    : 'Try Another Way - Send OTP'
                  }
                </Button>
              </Box>
            </Box>
          )}

          {/* OTP Step */}
          {forgotPasswordStep === 'otp' && (
            <Box
              component="form"
              onSubmit={forgotPasswordForm.handleSubmit(handleForgotPasswordOtp)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                mt: 2
              }}
            >
              <Typography variant="body1" sx={{ textAlign: 'center', mb: 2, color: '#666' }}>
                We've sent a 6-digit OTP to <strong>{forgotPasswordEmail}</strong>
              </Typography>
              
              <TextField
                fullWidth
                label="Enter OTP"
                type="text"
                autoFocus
                inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } }}
                {...forgotPasswordForm.register('otp', { 
                  required: 'OTP is required',
                  pattern: {
                    value: /^\d{6}$/,
                    message: 'Please enter a valid 6-digit OTP'
                  }
                })}
                error={!!forgotPasswordForm.formState.errors.otp}
                helperText={forgotPasswordForm.formState.errors.otp?.message}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&:hover fieldset': {
                      borderColor: '#5c0f2f',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#5c0f2f',
                      borderWidth: 2
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#5c0f2f'
                  }
                }}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isForgotPasswordLoading}
                sx={{
                  mt: 2,
                  py: 2,
                  background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #4a0d26, #5c0f2f)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(92,15,47,0.3)'
                  },
                  '&:disabled': {
                    background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                    opacity: 0.7
                  },
                  fontSize: '1.1rem',
                  textTransform: 'none',
                  borderRadius: '12px',
                  transition: 'all 0.3s',
                  fontWeight: 600
                }}
              >
                {isForgotPasswordLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                    Verifying...
                  </Box>
                ) : (
                  'Verify OTP'
                )}
              </Button>
            </Box>
          )}

          {/* Reset Password Step */}
          {forgotPasswordStep === 'reset' && (
            <Box
              component="form"
              onSubmit={forgotPasswordForm.handleSubmit(handleResetPassword)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                mt: 2
              }}
            >
              <Typography variant="body1" sx={{ textAlign: 'center', mb: 2, color: '#666' }}>
                Create a new password for your account
              </Typography>
              
              <TextField
                fullWidth
                label="New Password"
                type={showNewPassword ? 'text' : 'password'}
                autoFocus
                {...forgotPasswordForm.register('password', { 
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters'
                  }
                })}
                error={!!forgotPasswordForm.formState.errors.password}
                helperText={forgotPasswordForm.formState.errors.password?.message}
                InputProps={{
                  startAdornment: <LockIcon sx={{ color: '#5c0f2f', mr: 1 }} />,
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                      sx={{ color: '#5c0f2f' }}
                    >
                      {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&:hover fieldset': {
                      borderColor: '#5c0f2f',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#5c0f2f',
                      borderWidth: 2
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#5c0f2f'
                  }
                }}
              />
              
              <TextField
                fullWidth
                label="Confirm New Password"
                type={showConfirmPassword ? 'text' : 'password'}
                {...forgotPasswordForm.register('confirmPassword', { 
                  required: 'Please confirm your password'
                })}
                error={!!forgotPasswordForm.formState.errors.confirmPassword}
                helperText={forgotPasswordForm.formState.errors.confirmPassword?.message}
                InputProps={{
                  startAdornment: <LockIcon sx={{ color: '#5c0f2f', mr: 1 }} />,
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      sx={{ color: '#5c0f2f' }}
                    >
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&:hover fieldset': {
                      borderColor: '#5c0f2f',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#5c0f2f',
                      borderWidth: 2
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#5c0f2f'
                  }
                }}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isForgotPasswordLoading}
                sx={{
                  mt: 2,
                  py: 2,
                  background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #4a0d26, #5c0f2f)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(92,15,47,0.3)'
                  },
                  '&:disabled': {
                    background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                    opacity: 0.7
                  },
                  fontSize: '1.1rem',
                  textTransform: 'none',
                  borderRadius: '12px',
                  transition: 'all 0.3s',
                  fontWeight: 600
                }}
              >
                {isForgotPasswordLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                    Resetting Password...
                  </Box>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LoginPage;
